import { getFunctions, httpsCallable } from 'firebase/functions';
import type { ImportResult, ImportFieldMapping } from '@/types';

const functions = getFunctions();

// ===========================================
// Types
// ===========================================

export interface ImportCompanyRow {
  name: string;
  type?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
  website?: string;
  notes?: string;
  salesRepName?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactTitle?: string;
}

interface ValidateResult {
  success: boolean;
  validRows: number;
  invalidRows: number;
  errors: { row: number; errors: string[] }[];
  warnings: { row: number; warnings: string[] }[];
  duplicates: { row: number; existingCompanyName: string }[];
}

interface TemplateResult {
  success: boolean;
  headers: string[];
  requiredHeaders: string[];
  sampleRow: ImportCompanyRow;
}

// ===========================================
// API Functions
// ===========================================

/**
 * Validate import data before executing
 */
export async function validateImport(rows: ImportCompanyRow[]): Promise<ValidateResult> {
  const validate = httpsCallable<{ rows: ImportCompanyRow[] }, ValidateResult>(
    functions,
    'crm_validateBulkImport'
  );

  const result = await validate({ rows });
  return result.data;
}

/**
 * Execute the bulk import
 */
export async function executeImport(
  rows: ImportCompanyRow[],
  skipDuplicates: boolean = true
): Promise<ImportResult> {
  const execute = httpsCallable<
    { rows: ImportCompanyRow[]; skipDuplicates: boolean },
    ImportResult
  >(functions, 'crm_executeBulkImport');

  const result = await execute({ rows, skipDuplicates });
  return result.data;
}

/**
 * Get import template info
 */
export async function getImportTemplate(): Promise<TemplateResult> {
  const getTemplate = httpsCallable<void, TemplateResult>(
    functions,
    'crm_getImportTemplate'
  );

  const result = await getTemplate();
  return result.data;
}

// ===========================================
// CSV Parsing Utilities
// ===========================================

/**
 * Parse CSV text into rows
 */
export function parseCSV(csvText: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  // Parse header row
  const headers = parseCSVLine(lines[0]);

  // Parse data rows
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    rows.push(row);
  }

  return { headers, rows };
}

/**
 * Parse a single CSV line, handling quoted values
 */
function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

/**
 * Map raw CSV rows to import format based on field mappings
 */
export function mapRowsToImportFormat(
  rows: Record<string, string>[],
  mappings: ImportFieldMapping[]
): ImportCompanyRow[] {
  return rows.map((row) => {
    const mapped: Record<string, string> = {};

    mappings.forEach((mapping) => {
      if (mapping.targetField && mapping.sourceColumn) {
        mapped[mapping.targetField] = row[mapping.sourceColumn] || '';
      }
    });

    return mapped as unknown as ImportCompanyRow;
  });
}

/**
 * Auto-detect field mappings based on header names
 */
export function autoDetectMappings(headers: string[]): ImportFieldMapping[] {
  const fieldPatterns: Record<string, RegExp[]> = {
    name: [/^(company.?)?name$/i, /^business.?name$/i, /^company$/i],
    type: [/^type$/i, /^business.?type$/i, /^industry$/i, /^category$/i],
    address: [/^address$/i, /^street$/i, /^street.?address$/i],
    city: [/^city$/i, /^town$/i],
    state: [/^state$/i, /^province$/i, /^st$/i],
    zip: [/^zip$/i, /^postal$/i, /^zip.?code$/i, /^postal.?code$/i],
    phone: [/^phone$/i, /^phone.?number$/i, /^tel$/i, /^telephone$/i],
    email: [/^email$/i, /^e-?mail$/i, /^company.?email$/i],
    website: [/^website$/i, /^web$/i, /^url$/i, /^site$/i],
    notes: [/^notes$/i, /^comments$/i, /^description$/i],
    salesRepName: [/^sales.?rep$/i, /^rep$/i, /^assigned.?to$/i, /^owner$/i],
    contactFirstName: [/^contact.?first$/i, /^first.?name$/i, /^contact$/i],
    contactLastName: [/^contact.?last$/i, /^last.?name$/i],
    contactEmail: [/^contact.?email$/i],
    contactPhone: [/^contact.?phone$/i],
    contactTitle: [/^contact.?title$/i, /^title$/i, /^position$/i, /^role$/i],
  };

  return headers.map((header) => {
    let targetField = '';

    for (const [field, patterns] of Object.entries(fieldPatterns)) {
      if (patterns.some((pattern) => pattern.test(header))) {
        targetField = field;
        break;
      }
    }

    return {
      sourceColumn: header,
      targetField: targetField,
    } as ImportFieldMapping;
  });
}

/**
 * Generate CSV template for download
 */
export function generateCSVTemplate(): string {
  const headers = [
    'name',
    'type',
    'address',
    'city',
    'state',
    'zip',
    'phone',
    'email',
    'website',
    'notes',
    'salesRepName',
    'contactFirstName',
    'contactLastName',
    'contactEmail',
    'contactPhone',
    'contactTitle',
  ];

  const sampleRow = [
    'ABC Plumbing',
    'Plumber',
    '123 Main St',
    'Phoenix',
    'AZ',
    '85001',
    '(602) 555-1234',
    'info@abcplumbing.com',
    'www.abcplumbing.com',
    'Great referral partner',
    'John Smith',
    'Jane',
    'Doe',
    'jane@abcplumbing.com',
    '(602) 555-5678',
    'Owner',
  ];

  return [headers.join(','), sampleRow.join(',')].join('\n');
}

/**
 * Download a string as a file
 */
export function downloadFile(content: string, filename: string, mimeType: string = 'text/csv') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
